import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '../../hooks/useAuth';
import { localize } from '../../i18n/script';
import { useSiteLanguage } from '../../i18n/useSiteLanguage';
import {
  promotionCodeApi,
  type PromotionCode,
  type PromotionCodeDetail,
} from '../../services/promotionCodeApi';

type FilterType = 'all' | 'active' | 'inactive';

function getCodeDisplayStatus(code: PromotionCode): 'active' | 'inactive' | 'expired' {
  if (!code.isActive) return 'inactive';
  if (code.validUntil && new Date(code.validUntil) < new Date()) return 'expired';
  return 'active';
}

export default function AdminPromotionCodes() {
  const siteLanguage = useSiteLanguage();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading, getToken } = useAuth();

  const [codes, setCodes] = useState<PromotionCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterType>('all');
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [viewingUsage, setViewingUsage] = useState<PromotionCode | null>(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    const fetchCodes = async () => {
      if (!isAuthenticated) return;

      try {
        setLoading(true);
        const token = await getToken();
        if (!token) throw new Error('No access token');
        const data = await promotionCodeApi.getAll(token);
        setCodes(data.items);
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load promotion codes';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchCodes();
  }, [isAuthenticated, getToken]);

  const handleToggleActive = async (code: PromotionCode) => {
    try {
      setTogglingId(code.id);
      const token = await getToken();
      if (!token) throw new Error('No access token');
      await promotionCodeApi.update(code.id, { isActive: !code.isActive }, token);
      setCodes((prev) => prev.map((c) => (c.id === code.id ? { ...c, isActive: !c.isActive } : c)));
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update status';
      setError(errorMessage);
    } finally {
      setTogglingId(null);
    }
  };

  const filteredCodes = codes.filter((code) => {
    if (filter === 'all') return true;
    if (filter === 'active') return code.isActive && getCodeDisplayStatus(code) !== 'expired';
    if (filter === 'inactive') return !code.isActive || getCodeDisplayStatus(code) === 'expired';
    return true;
  });

  const getFilterCount = (f: FilterType) => {
    if (f === 'all') return codes.length;
    if (f === 'active')
      return codes.filter((c) => c.isActive && getCodeDisplayStatus(c) !== 'expired').length;
    if (f === 'inactive')
      return codes.filter((c) => !c.isActive || getCodeDisplayStatus(c) === 'expired').length;
    return 0;
  };

  const formatDiscount = (code: PromotionCode) => {
    if (code.discountType === 'FIXED_AMOUNT') {
      return `$${(code.discountValue / 100).toFixed(2)} off`;
    }
    return `${code.discountValue}% off`;
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading promotion codes...</p>
        </div>
      </div>
    );
  }

  return (
    <AdminLayout
      title="Promotion Codes"
      headerRight={
        <Link
          to="/admin/promotion-codes/new"
          className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
        >
          <span className="mr-2">+</span>
          Create Code
        </Link>
      }
    >
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">
            Dismiss
          </button>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="bg-white rounded-lg shadow-sm mb-6">
        <div>
          <nav className="flex -mb-px overflow-x-auto">
            {(['all', 'active', 'inactive'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setFilter(tab)}
                className={`px-6 py-3 text-sm font-medium border-b-2 whitespace-nowrap flex-shrink-0 ${
                  filter === tab
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
                <span className="ml-2 text-xs bg-gray-100 px-2 py-0.5 rounded-full">
                  {getFilterCount(tab)}
                </span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Codes Table */}
      {filteredCodes.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <div className="text-gray-400 text-5xl mb-4">🏷️</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No promotion codes found</h3>
          <p className="text-gray-500 mb-6">
            {filter === 'all'
              ? 'Create your first promotion code to get started.'
              : `No ${filter} promotion codes.`}
          </p>
          {filter === 'all' && (
            <Link
              to="/admin/promotion-codes/new"
              className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
            >
              Create Code
            </Link>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Code
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Discount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Activities
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Usage / Per-User Limit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Valid Period
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredCodes.map((code) => {
                  const displayStatus = getCodeDisplayStatus(code);
                  return (
                    <tr key={code.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-mono font-bold text-gray-900">{code.code}</span>
                        {code.description && (
                          <p className="text-xs text-gray-500 mt-0.5 max-w-[200px] truncate">
                            {code.description}
                          </p>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDiscount(code)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span>
                          {code.activities.length} activit
                          {code.activities.length === 1 ? 'y' : 'ies'}
                        </span>
                        {code.activities.length > 0 && (
                          <p className="text-xs text-gray-400 mt-0.5 max-w-[180px] truncate">
                            {localize(
                              code.activities[0].activity.titleCN,
                              code.activities[0].activity.titleEN,
                              siteLanguage
                            )}
                            {code.activities.length > 1 && ` +${code.activities.length - 1}`}
                          </p>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span>{code._count.usages} used</span>
                        <span className="text-gray-500 ml-1">
                          ({code.maxUsesPerUser ? `${code.maxUsesPerUser}/user` : 'unlimited'})
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <StatusBadge status={displayStatus} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {code.validFrom || code.validUntil ? (
                          <>
                            {formatDate(code.validFrom) ?? 'Start'} &ndash;{' '}
                            {formatDate(code.validUntil) ?? 'No end'}
                          </>
                        ) : (
                          'No expiry'
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                        <div className="flex items-center justify-end gap-2">
                          {code._count.usages > 0 && (
                            <button
                              onClick={() => setViewingUsage(code)}
                              className="text-blue-600 hover:text-blue-800 hover:underline"
                            >
                              Usage
                            </button>
                          )}
                          <Link
                            to={`/admin/promotion-codes/${code.id}/edit`}
                            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            Edit
                          </Link>
                          <button
                            onClick={() => handleToggleActive(code)}
                            disabled={togglingId === code.id}
                            className={`px-3 py-1.5 text-sm border rounded-lg transition-colors ${
                              code.isActive
                                ? 'text-yellow-600 border-yellow-200 hover:bg-yellow-50'
                                : 'text-green-600 border-green-200 hover:bg-green-50'
                            } ${togglingId === code.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            {togglingId === code.id
                              ? '...'
                              : code.isActive
                                ? 'Deactivate'
                                : 'Activate'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Usage Modal */}
      {viewingUsage && (
        <UsageModal code={viewingUsage} getToken={getToken} onClose={() => setViewingUsage(null)} />
      )}
    </AdminLayout>
  );
}

function StatusBadge({ status }: { status: string }) {
  const statusConfig: Record<string, { bg: string; text: string; label: string }> = {
    active: { bg: 'bg-green-100', text: 'text-green-800', label: 'Active' },
    inactive: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Inactive' },
    expired: { bg: 'bg-red-100', text: 'text-red-800', label: 'Expired' },
  };

  const config = statusConfig[status] || statusConfig.inactive;

  return (
    <span
      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${config.bg} ${config.text}`}
    >
      {config.label}
    </span>
  );
}

function UsageModal({
  code,
  getToken,
  onClose,
}: {
  code: PromotionCode;
  getToken: () => Promise<string | undefined>;
  onClose: () => void;
}) {
  const siteLanguage = useSiteLanguage();
  const [usages, setUsages] = useState<PromotionCodeDetail['usages']>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUsages = async () => {
      try {
        setLoading(true);
        const token = await getToken();
        if (!token) throw new Error('No access token');
        const detail = await promotionCodeApi.getById(code.id, token);
        setUsages(detail.usages || []);
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load usage data';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchUsages();
  }, [code.id, getToken]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Usage History</h2>
              <p className="text-sm text-gray-500 mt-1">
                Code: <span className="font-mono font-bold">{code.code}</span> &middot;{' '}
                {code._count.usages} use{code._count.usages !== 1 ? 's' : ''}
              </p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">
              ✕
            </button>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
              <p className="text-sm text-gray-500">Loading usage data...</p>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
              {error}
            </div>
          ) : usages.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No usage records found.</p>
          ) : (
            <div className="space-y-3">
              {usages.map((usage) => (
                <div
                  key={usage.id}
                  className="bg-gray-50 rounded-lg p-4 flex items-center justify-between"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {usage.user.name || usage.email}
                    </p>
                    {usage.user.name && <p className="text-xs text-gray-500">{usage.email}</p>}
                    <p className="text-xs text-gray-500 mt-1">
                      {localize(usage.activity.titleCN, usage.activity.titleEN, siteLanguage)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-green-700">
                      -${(usage.discountAmount / 100).toFixed(2)}
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(usage.createdAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Footer */}
          <div className="mt-6 pt-4">
            <button
              onClick={onClose}
              className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
